/**
 * InternalModelsPanel — super-admin UI for per-tenant overrides of internal
 * service models. Currently exposes the Templates (insight-extraction) model.
 *
 * For each purpose the super-admin picks a single model (or "platform default").
 * Storage: tenant.tenant_settings.internal_models[<purpose>] = "<model id>".
 * Data sources:
 *   - GET /ai/models/?view=versions                     → concrete model catalog
 *   - GET/PATCH /super-admin/tenants/<id>/internal-models → per-tenant config
 * Internal/RAG models stay CONCRETE (unlike user-facing pickers, which offer
 * model families) — the versions view is the admin-only endpoint that still
 * returns concrete catalog entries. The catalog comes from
 * config/ai_models.json — adding a model there surfaces it here automatically
 * (no hardcoded list).
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, Select, Spin, Empty, message as antdMessage, Space, Tag } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';

interface PlatformModel {
  id: string; // concrete model id, e.g. 'claude-sonnet-5'
  name: string;
  provider: string;
  provider_name?: string;
  family?: string; // family key this version belongs to, e.g. 'sonnet'
}

interface FamilyDef {
  name: string;
  provider?: string;
  default_version?: string;
  enabled?: boolean;
  description?: string;
}

interface PurposeConfig {
  label: string;
  selected_model: string | null;
  is_default: boolean;
  platform_default: string;
}

interface InternalModelsPanelProps {
  tenantId: string;
}

export function InternalModelsPanel({ tenantId }: InternalModelsPanelProps) {
  const { t } = useLanguage();
  const [models, setModels] = useState<PlatformModel[]>([]);
  const [families, setFamilies] = useState<Record<string, FamilyDef>>({});
  const [purposes, setPurposes] = useState<Record<string, PurposeConfig>>({});
  const [loading, setLoading] = useState(true);
  const [savingPurpose, setSavingPurpose] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [modelsRes, cfgRes] = await Promise.all([
          // Internal models are CONCRETE ids — the default /ai/models/ view
          // now returns families, so use the admin-only versions view.
          api.get('/ai/models/?view=versions'),
          api.get(`/super-admin/tenants/${tenantId}/internal-models`),
        ]);
        if (cancelled) return;
        setModels(modelsRes.data?.models || []);
        setFamilies(modelsRes.data?.families || {});
        setPurposes(cfgRes.data?.purposes || {});
      } catch (err: any) {
        antdMessage.error(`Failed to load internal models: ${err?.message || err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId]);

  const grouped = useMemo(() => {
    const out: Array<{ provider: string; provider_name: string; models: PlatformModel[] }> = [];
    const seen: Record<string, number> = {};
    for (const m of models) {
      if (!(m.provider in seen)) {
        seen[m.provider] = out.length;
        out.push({ provider: m.provider, provider_name: m.provider_name || m.provider, models: [] });
      }
      out[seen[m.provider]].models.push(m);
    }
    return out;
  }, [models]);

  const modelName = (id: string) => models.find(m => m.id === id)?.name || id;

  // The display name of the family a concrete model belongs to (if known).
  const familyName = (m: PlatformModel): string | undefined =>
    m.family ? families[m.family]?.name || m.family : undefined;

  // Concrete model option: name + its family as muted secondary info.
  const modelLabel = (m: PlatformModel) => (
    <span>
      {m.name}
      {familyName(m) && (
        <span style={{ fontSize: 12, opacity: 0.6, marginInlineStart: 8 }}>
          {familyName(m)}
        </span>
      )}
    </span>
  );

  // Options grouped by provider, with a leading "platform default" entry.
  const optionsFor = (cfg: PurposeConfig) => ([
    {
      value: '',
      label: `${t('superAdmin.internalModels.platformDefault') || 'Platform default'} (${modelName(cfg.platform_default)})`,
    },
    ...grouped.map(group => ({
      label: group.provider_name,
      options: group.models.map(m => ({ value: m.id, label: modelLabel(m) })),
    })),
  ]);

  async function persist(purpose: string, value: string) {
    setSavingPurpose(purpose);
    // Optimistic update for snappy UI.
    setPurposes(prev => ({
      ...prev,
      [purpose]: {
        ...prev[purpose],
        selected_model: value || null,
        is_default: !value,
      },
    }));
    try {
      const res = await api.patch(`/super-admin/tenants/${tenantId}/internal-models`, {
        purpose,
        model: value || null,
      });
      const d = res.data || {};
      setPurposes(prev => ({
        ...prev,
        [purpose]: {
          ...prev[purpose],
          selected_model: d.selected_model ?? (value || null),
          is_default: d.is_default ?? !value,
        },
      }));
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSavingPurpose(null);
    }
  }

  if (loading) return <Spin />;
  if (models.length === 0) return <Empty description={t('superAdmin.models.noPlatformModels')} />;

  const purposeKeys = Object.keys(purposes);
  if (purposeKeys.length === 0) return null;

  return (
    <Card
      size="small"
      title={t('superAdmin.internalModels.cardTitle') || 'Internal-service models'}
      style={{ marginTop: 16 }}
    >
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 14 }}>
        {t('superAdmin.internalModels.description') ||
          'Choose which model this tenant uses for internal extraction tasks. Leave on platform default unless this tenant needs a specific model.'}
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {purposeKeys.map(purpose => {
          const cfg = purposes[purpose];
          if (!cfg) return null;
          return (
            <div
              key={purpose}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                borderTop: '1px solid var(--border-primary, rgba(15,23,42,0.08))',
                paddingTop: 14,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 14, minWidth: 200 }} dir="auto">{cfg.label}</span>
              {cfg.is_default && (
                <Tag color="default">
                  {t('superAdmin.internalModels.usingDefault') || 'platform default'}
                </Tag>
              )}
              <Select
                size="small"
                style={{ minWidth: 280 }}
                value={cfg.is_default ? '' : (cfg.selected_model || '')}
                loading={savingPurpose === purpose}
                onChange={(v) => persist(purpose, v)}
                options={optionsFor(cfg)}
              />
            </div>
          );
        })}
      </Space>
    </Card>
  );
}
